import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router-dom';
import LoadingBar from 'react-top-loading-bar';

import Toast from '@components/Toast/Toast';
import { IRoute } from '@interfaces/routes';
import privateRoutes from '@routes/private';
import publicRoutes from '@routes/public';
import urls from '@routes/urls';
import { handleRefreshToken } from '@store/features/auth/token/actions';
import { authTokenSelector } from '@store/features/auth/token/selectors';
import { updateTokens } from '@store/features/auth/token/slice';
import { handleGetUserProfile } from '@store/features/auth/user/actions';
import { userDataSelector } from '@store/features/auth/user/selectors';
import {
  canAccessMedicationLookupSelector,
  canAccessSupplementInquirySelector,
  dashboardOrgDataSelector,
} from '@store/features/dashboardOrgInfo/selectors';
import { handleGetDashboardOrgInfo } from '@store/features/dashboardOrgInfo/actions';
import { loadingCountSelector } from '@store/features/loading/selectors';
import useDispatch from '@store/hooks/useDispatch';
import { colors } from '@utils/constants';

import { getAuthTokenFromLocalStorage, getRefreshTokenFromLocalStorage } from '@utils/token';
import Footer from './Footer/Footer';
import Header from './Header/Header';
import ProtectedRoute from './ProtectedRoute/ProtectedRoute';

const Root: FC = () => {
  const dispatch = useDispatch();
  const user = useSelector(userDataSelector);
  const loading = useSelector(loadingCountSelector);
  const storageAuthToken = useSelector(authTokenSelector);
  const orgInfo = useSelector(dashboardOrgDataSelector);
  const canAccessSupplementInquiry = useSelector(canAccessSupplementInquirySelector);
  const canAccessMedicationLookup = useSelector(canAccessMedicationLookupSelector);

  const authToken = getAuthTokenFromLocalStorage();
  const refreshToken = getRefreshTokenFromLocalStorage();

  const location = useLocation();

  const [isTokenRefreshed, setIsTokenRefreshed] = useState<boolean>(false);

  const canShowRiskList = useMemo(() => {
    if (typeof user?.showRiskList === 'boolean' && typeof orgInfo?.showRiskList === 'boolean') {
      return user.showRiskList || orgInfo.showRiskList;
    }
    return false;
  }, [user, orgInfo]);

  const filteredPrivateRoutes = useMemo(
    () =>
      privateRoutes.filter((route: IRoute) => {
        if (!canAccessSupplementInquiry && route.path === urls.inquiryDraft) {
          return false;
        }
        if (!canShowRiskList && route.path === urls.highRiskSupplements) {
          return false;
        }
        if (!canAccessMedicationLookup && route.path === urls.medications) {
          return false;
        }
        return true;
      }),
    [canShowRiskList, canAccessSupplementInquiry, canAccessMedicationLookup],
  );

  useEffect(() => {
    if (authToken && refreshToken) {
      dispatch(
        updateTokens({
          authToken,
          refreshToken,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (storageAuthToken) {
      dispatch(handleGetUserProfile());
    }
  }, [dispatch, storageAuthToken]);

  useEffect(() => {
    if (user && !user.organizationId && !isTokenRefreshed) {
      dispatch(handleRefreshToken());
      setIsTokenRefreshed(true);
    }

    if (user) {
      dispatch(handleGetDashboardOrgInfo());
    }
  }, [dispatch, user]);

  const loadingRef = useRef(null);

  useEffect(() => {
    if (loadingRef?.current) {
      loading > 0
        ? // @ts-ignore
          loadingRef.current.continuousStart()
        : // @ts-ignore
          loadingRef.current.complete();
    }
  }, [loading]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const rootDiv = document.getElementById('root');
    if (rootDiv) {
      rootDiv.style.minHeight = `${window.innerHeight}px`;
    }
  }, []);

  return (
    <>
      <LoadingBar color={colors.primary} ref={loadingRef} />

      <Toast />

      {location.pathname !== urls.login && <Header />}

      <Routes>
        {filteredPrivateRoutes.map((route: IRoute, i: number) => (
          <Route
            key={i}
            path={route.path}
            element={
              <ProtectedRoute admin={route.admin}>
                <route.component />
              </ProtectedRoute>
            }
          />
        ))}

        {publicRoutes.map((route: IRoute, i: number) => (
          <Route key={i} path={route.path} element={<route.component />} />
        ))}
      </Routes>

      <Footer />
    </>
  );
};

export default Root;
